import React from 'react';
import {useNavigate} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiTarget,FiTrendingUp,FiUsers,FiBookOpen,FiStar,FiAward,FiZap,FiCheckCircle,FiUser,FiRefreshCw}=FiIcons;

const EnhancedWelcome=({setCurrentStep})=> {
  const navigate=useNavigate();
  
  const handleGetStarted=()=> {
    setCurrentStep(1);
    navigate('/basic-info');
  };
  
  const handleContinue=()=> {
    const savedStep=localStorage.getItem('marketingPlanStep');
    if (savedStep && parseInt(savedStep) > 0) {
      // Continue from the last saved step
      setCurrentStep(parseInt(savedStep));
      navigate(savedStep === '1' ? '/basic-info' : 
               savedStep === '2' ? '/niche' : 
               savedStep === '3' ? '/goals' : 
               savedStep === '4' ? '/action-plan' : 
               savedStep === '5' ? '/marketing-analysis' : 
               '/complete-plan');
    } else {
      handleGetStarted();
    }
  };

  const features=[
    {icon: FiTarget,title: 'Find Your Focus',description: 'Clarify what makes your teaching unique and what kind of students you want to reach.',color: 'blue'},
    {icon: FiTrendingUp,title: 'Set Meaningful Goals',description: 'Create clear,realistic,and motivating goals that match your teaching journey.',color: 'purple'},
    {icon: FiUsers,title: 'Know Your Audience',description: 'Understand who your ideal students are — and how to reach them consistently.',color: 'green'},
    {icon: FiBookOpen,title: 'Build Your Growth Map',description: 'Receive a complete,personalized marketing plan that fits your teaching style and schedule.',color: 'orange'}
  ];

  const benefits=[
    'Attract more of the right students — not just more students',
    'Build a steady income with less stress',
    'Create multiple ways to earn (classes,courses,digital products)',
    'Grow your reputation and visibility as an expert',
    'Stay balanced while growing your business sustainably'
  ];

  return (
    <div className="max-w-6xl mx-auto">
      <motion.div 
        initial={{opacity: 0,y: 30}}
        animate={{opacity: 1,y: 0}}
        className="bg-white rounded-2xl shadow-xl p-8 mb-8"
      >
        {/* Hero Section */}
        <div className="text-center mb-12">
          <motion.div 
            initial={{scale: 0}}
            animate={{scale: 1}}
            transition={{delay: 0.2}}
            className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full mb-6"
          >
            <SafeIcon icon={FiBookOpen} className="text-4xl text-white" />
          </motion.div>
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            Transform the Way You Grow as an Independent Educator
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Join a growing community of independent teachers and tutors using PlanMyGrowth to attract more students and build thriving,sustainable teaching businesses — one simple step at a time.
          </p>
          
          {/* Stats */}
          <div className="grid grid-cols-3 gap-8 mb-8 max-w-2xl mx-auto">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-1">5,000+</div>
              <div className="text-sm text-gray-600">Educators Growing With SuperTeacher</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600 mb-1">87%</div>
              <div className="text-sm text-gray-600">Report Better Student Engagement</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600 mb-1">4.9/5</div>
              <div className="text-sm text-gray-600">Satisfaction Score</div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <motion.button 
              whileHover={{scale: 1.05}}
              whileTap={{scale: 0.95}}
              onClick={handleGetStarted}
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-10 py-4 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transition-all"
            >
              Start Planning Your Growth — It's Free
            </motion.button>
            
            {(localStorage.getItem('marketingPlanStep') && parseInt(localStorage.getItem('marketingPlanStep')) > 0) && (
              <motion.button 
                whileHover={{scale: 1.05}}
                whileTap={{scale: 0.95}}
                onClick={handleContinue}
                className="bg-gradient-to-r from-green-500 to-teal-600 text-white px-10 py-4 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transition-all"
              >
                Continue Where You Left Off
              </motion.button>
            )}
          </div>
          
          <p className="text-sm text-gray-500 mt-4">
            Takes about 20 minutes • No credit card required • Built for independent educators
          </p>
        </div>
        
        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {features.map((feature,index)=> (
            <motion.div 
              key={index}
              initial={{opacity: 0,y: 20}}
              animate={{opacity: 1,y: 0}}
              transition={{delay: 0.3 + index * 0.1}}
              className="flex flex-col items-center text-center p-6 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all"
            >
              <div className={`w-16 h-16 bg-${feature.color}-100 rounded-full flex items-center justify-center mb-4`}>
                <SafeIcon icon={feature.icon} className={`text-2xl text-${feature.color}-600`} />
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">{feature.title}</h3>
              <p className="text-sm text-gray-600">{feature.description}</p>
            </motion.div>
          ))}
        </div>
        
        {/* Benefits Section */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            <SafeIcon icon={FiStar} className="inline text-yellow-500 mr-2" />
            What You'll Achieve
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {benefits.map((benefit,index)=> (
              <motion.div 
                key={index}
                initial={{opacity: 0,x: -20}}
                animate={{opacity: 1,x: 0}}
                transition={{delay: 0.5 + index * 0.1}}
                className="flex items-center space-x-3"
              >
                <SafeIcon icon={FiCheckCircle} className="text-green-500 flex-shrink-0" />
                <span className="text-gray-700">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
        
        {/* How It Works */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-8 text-center">How It Works</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              {step: 1,title: 'Tell Us About Your Teaching',desc: 'Share your background,subject area,and current goals'},
              {step: 2,title: 'Discover Your Strengths',desc: 'Find your niche and define what sets your lessons apart'},
              {step: 3,title: 'Plan Your Next Steps',desc: 'Set short- and long-term goals to guide your marketing'},
              {step: 4,title: 'Get Your Personalized Growth Plan',desc: 'Receive a step-by-step strategy you can act on right away'}
            ].map((item,index)=> (
              <div key={index} className="text-center">
                <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mx-auto mb-3">
                  {item.step}
                </div>
                <h3 className="font-semibold text-gray-800 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
        
        {/* Testimonials */}
        <div className="bg-gray-50 rounded-xl p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Success Stories</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {name: 'Sarah Johnson',role: 'Math Tutor',content: 'PlanMyGrowth helped me clarify my offer and attract the right students — I\'m finally running my business,not chasing it.'},
              {name: 'Michael Chen',role: 'Piano Teacher',content: 'Once I defined my audience,everything clicked. Now I have more inquiries than I can handle.'},
              {name: 'Emily Davis',role: 'Language Coach',content: 'This is the first tool that actually understands how educators think. Simple,practical,and it works.'}
            ].map((testimonial,index)=> (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center mb-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <SafeIcon icon={FiUser} className="text-blue-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-800">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.role}</div>
                  </div>
                </div>
                <p className="text-gray-700 text-sm mb-3">"{testimonial.content}"</p>
                <div className="flex">
                  {[...Array(5)].map((_,i)=> (
                    <SafeIcon key={i} icon={FiStar} className="text-yellow-400 text-sm" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Final CTA */}
        <div className="text-center mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            <SafeIcon icon={FiZap} className="inline text-yellow-500 mr-2" />
            Ready to Grow Your Teaching Business?
          </h2>
          <p className="text-gray-600 mb-6">
            Join thousands of educators who've built confidence,clarity,and consistent growth — with PlanMyGrowth.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <motion.button 
              whileHover={{scale: 1.05}}
              whileTap={{scale: 0.95}}
              onClick={handleGetStarted}
              className="bg-gradient-to-r from-green-500 to-blue-600 text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all"
            >
              Get Started Now — It's Free
            </motion.button>
            
            {(localStorage.getItem('marketingPlanStep') && parseInt(localStorage.getItem('marketingPlanStep')) > 0) && (
              <motion.button 
                whileHover={{scale: 1.05}}
                whileTap={{scale: 0.95}}
                onClick={handleContinue}
                className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all"
              >
                Continue Your Plan
              </motion.button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default EnhancedWelcome;